import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, BookOpen, Filter, Info, CreditCard, Sparkles, ShoppingCart, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import SortableGrid from '../components/SortableGrid';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Books = () => {
  const { addToCart } = useCart();
  const { user, isAdmin, getAuthHeader } = useAuth();
  const [books, setBooks] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [previewBook, setPreviewBook] = useState(null);
  const [previewPages, setPreviewPages] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [sortBy, setSortBy] = useState('featured');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const pricingRef = useRef(null);

  const loadBooks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/books`);
      setBooks(response.data);
    } catch (error) {
      toast.error('Failed to load books');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPricing = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pricing`);
      setPricing(response.data);
    } catch (error) {
      toast.error('Failed to load pricing');
    }
  }, []);

  useEffect(() => {
    loadBooks();
    loadPricing();
    
    if (window.location.hash === '#pricing') {
      setTimeout(() => {
        pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [loadBooks, loadPricing]);

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const openPreview = useCallback(async (book) => {
    setPreviewBook(book);
    setPreviewPages([]);
    setPreviewPage(0);
    setPreviewLoading(true);
    
    const pages = [];
    for (let i = 1; i <= Math.min(6, book.pages || 6); i++) {
      try {
        const res = await axios.get(`${API_URL}/books/${book.id}/preview/${i}`, { responseType: 'blob' });
        pages.push(URL.createObjectURL(res.data));
      } catch {
        break;
      }
    }
    setPreviewPages(pages);
    setPreviewLoading(false);
  }, []);

  const closePreview = useCallback(() => {
    previewPages.forEach(url => URL.revokeObjectURL(url));
    setPreviewBook(null);
    setPreviewPages([]);
    setPreviewPage(0);
  }, [previewPages]);

  const sortedBulkDiscounts = useMemo(() =>
    pricing?.bulkDiscounts ? [...pricing.bulkDiscounts].sort((a, b) => a.quantity - b.quantity) : [],
    [pricing?.bulkDiscounts]
  );

  const adminCanReorder = isAdmin && isAdmin();

  const handleReorder = useCallback(async (newOrder) => {
    setBooks(newOrder);
    try {
      await axios.put(`${API_URL}/admin/books/reorder`, {
        order: newOrder.map(b => b.id)
      }, { headers: getAuthHeader() });
      toast.success('Book order saved');
    } catch {
      toast.error('Failed to save order');
    }
  }, [getAuthHeader]);
  
  const categories = ['all', ...new Set(books.map(book => book.category))];
  
  const filteredAndSortedBooks = books
    .filter(book => categoryFilter === 'all' || book.category === categoryFilter)
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return b.featured - a.featured;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading books...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Book Collection
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Explore thrilling adventures that make learning irresistible for young minds
          </p>
          
          {/* Pricing Notice */}
          <div className="max-w-3xl mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm text-blue-900 font-semibold mb-1">
                  📚 Hardcopy Pricing Information
                </p>
                <p className="text-sm text-blue-800">
                  The prices shown below are for physical hardcopy book orders. 
                  We also offer flexible digital subscription options for instant access to our entire collection.
                </p>
              </div>
            </div>
            <div className="mt-3 flex justify-center">
              <Button 
                onClick={scrollToPricing}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                View Pricing & Subscription Options
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center space-x-2 flex-1">
            <Filter className="h-5 w-5 text-gray-600" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Books Grid */}
        <SortableGrid
          items={filteredAndSortedBooks}
          onReorder={handleReorder}
          enabled={adminCanReorder && categoryFilter === 'all' && sortBy === 'featured'}
          className="grid md:grid-cols-3 lg:grid-cols-4 gap-6"
          renderItem={(book) => (
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 group">
              <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" style={{ minHeight: '320px' }}>
                <img
                  src={book.coverImage}
                  alt={book.title}
                  loading="lazy"
                  className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-800 shadow-md">
                  ${book.price}
                </div>
                {book.featured && (
                  <div className="absolute top-3 left-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                    BESTSELLER
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className={`h-3 w-3 ${i < Math.floor(book.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <span className="text-xs text-gray-600">({book.reviews})</span>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1 line-clamp-2">{book.title}</h3>
                <p className="text-sm text-gray-600 mb-1">{book.author}</p>
                <p className="text-xs text-blue-600 mb-3">{book.ageRange}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => addToCart(book)}
                    size="sm"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    data-testid={`add-to-cart-${book.id}`}
                  >
                    Add to Cart
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPreview(book)}
                    data-testid={`preview-btn-${book.id}`}
                  >
                    <BookOpen className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        />

        {filteredAndSortedBooks.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No books found matching your filters.</p>
          </div>
        )}

        {/* Pricing & Subscription Information Section */}
        <div id="pricing" ref={pricingRef} className="mt-16 pt-12 border-t border-gray-200">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Pricing & Subscription Options
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the perfect plan for your learning journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Hardcopy Purchase */}
            <Card className="border-2 border-gray-200 hover:border-blue-400 transition-all">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <ShoppingCart className="h-8 w-8 text-blue-600" />
                  <h3 className="text-2xl font-bold text-gray-800">Hardcopy Purchase</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Order physical copies of individual books delivered to your door.
                </p>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="mb-2">
                    <p className="text-sm text-blue-900 font-semibold mb-2">Individual Book Pricing</p>
                    <p className="text-blue-800 text-sm">
                      Each book is individually priced - see the price displayed on each book cover above. Prices vary by book.
                    </p>
                  </div>
                  {sortedBulkDiscounts.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <p className="text-sm font-semibold text-blue-900 mb-2">Bulk Discounts:</p>
                      <div className="space-y-1">
                        {sortedBulkDiscounts.map((tier) => (
                          <div key={`discount-${tier.quantity}`} className="text-sm text-blue-800">
                            • Buy {tier.quantity}+ books: <span className="font-semibold">{tier.discount}% off</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span className="text-gray-700">Beautiful printed hardcover books</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span className="text-gray-700">Perfect for home libraries and gifts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span className="text-gray-700">Free shipping on orders over $50</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span className="text-gray-700">Bulk discounts available</span>
                  </li>
                </ul>
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Browse Books Above
                </Button>
              </CardContent>
            </Card>

            {/* Digital Subscription */}
            <Card className="border-2 border-purple-200 hover:border-purple-400 transition-all bg-gradient-to-br from-purple-50 to-blue-50">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-8 w-8 text-purple-600" />
                  <h3 className="text-2xl font-bold text-gray-800">Digital Subscription</h3>
                  <span className="ml-auto bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    BEST VALUE
                  </span>
                </div>
                <p className="text-gray-600 mb-6">
                  Unlimited access to our entire digital library with instant reading.
                </p>
                <div className="space-y-4 mb-6">
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-bold text-purple-600">${pricing?.allBooksMonthly || '29.99'}</span>
                      <span className="text-gray-600">/ month</span>
                    </div>
                    <p className="text-sm text-gray-600">Access to all books</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-purple-400">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-bold text-purple-600">${pricing?.allBooksYearly || '299.99'}</span>
                      <span className="text-gray-600">/ year</span>
                      <span className="ml-auto bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                        Save 17%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">12 months of unlimited access</p>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span className="text-gray-700">Instant access to entire collection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span className="text-gray-700">Read on any device, anywhere</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span className="text-gray-700">Interactive flip-page reader</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span className="text-gray-700">Downloadable activity sheets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span className="text-gray-700">Teacher companion guides</span>
                  </li>
                </ul>
                <Button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                  Start Subscription
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              How It Works
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <h4 className="font-bold text-gray-800 mb-2">Choose Your Option</h4>
                <p className="text-sm text-gray-600">
                  Select individual hardcopy books or subscribe for digital access to the entire collection
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">2</span>
                </div>
                <h4 className="font-bold text-gray-800 mb-2">Complete Purchase</h4>
                <p className="text-sm text-gray-600">
                  Secure checkout with flexible payment options. Use coupon codes for additional discounts
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h4 className="font-bold text-gray-800 mb-2">Start Learning</h4>
                <p className="text-sm text-gray-600">
                  Physical books ship within 2-3 days. Digital access is instant - start reading immediately!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Book Preview Dialog */}
      <Dialog open={!!previewBook} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="book-preview-dialog">
          {previewBook && (
            <>
              {/* Header with cover */}
              <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 pb-4">
                <button
                  onClick={closePreview}
                  className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200 z-10"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
                <div className="flex gap-5">
                  <img
                    src={previewBook.coverImage}
                    alt={previewBook.title}
                    className="w-28 h-40 object-contain rounded-lg shadow-lg flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">{previewBook.title}</h2>
                    <p className="text-sm text-gray-600 mb-1">by {previewBook.author}</p>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{previewBook.category}</span>
                      <span className="text-xs text-gray-500">{previewBook.ageRange}</span>
                      <span className="text-xs text-gray-500">{previewBook.pages} pages</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={`preview-star-${i}`} className={`h-3.5 w-3.5 ${i < Math.floor(previewBook.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({previewBook.reviews})</span>
                    </div>
                    <div className="relative">
                      <div
                        className="max-h-28 border rounded-md p-2 bg-white/50"
                        style={{
                          overflowY: 'auto',
                          scrollbarWidth: 'auto',
                          scrollbarColor: '#9ca3af #e5e7eb',
                        }}
                      >
                        <p className="text-sm text-gray-700">{previewBook.description}</p>
                      </div>
                      {previewBook.description && previewBook.description.length > 150 && (
                        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white/90 to-transparent rounded-b-md pointer-events-none" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Page Preview */}
              <div className="px-6 py-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Preview Pages</h3>
                {previewLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : previewPages.length > 0 ? (
                  <div className="relative">
                    <div className="bg-gray-50 rounded-lg overflow-hidden border flex items-center justify-center" style={{ minHeight: '300px' }}>
                      {previewPage < previewPages.length ? (
                        <img
                          src={previewPages[previewPage]}
                          alt={`Page ${previewPage + 1}`}
                          className="max-w-full max-h-[350px] object-contain"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center px-8 py-12">
                          <BookOpen className="h-14 w-14 text-blue-500 mb-4" />
                          <h3 className="text-xl font-bold text-gray-800 mb-2">Enjoying the story so far?</h3>
                          <p className="text-gray-600 mb-6 max-w-sm">
                            Why not buy the book or subscribe to read the rest!
                          </p>
                          <div className="flex flex-col gap-3 w-full max-w-xs">
                            <Button
                              className="w-full bg-gradient-to-r from-blue-600 to-blue-700"
                              onClick={() => { addToCart(previewBook); closePreview(); }}
                            >
                              <ShoppingCart className="h-4 w-4 mr-2" /> Buy Now - ${previewBook.price}
                            </Button>
                            <Button asChild variant="outline" className="w-full">
                              <Link to="/books#pricing" onClick={closePreview}>
                                View Subscription Options
                              </Link>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={previewPage === 0}
                        onClick={() => setPreviewPage(p => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                      </Button>
                      <span className="text-sm text-gray-500">
                        {previewPage < previewPages.length
                          ? `Page ${previewPage + 1} of ${previewPages.length + 1}`
                          : `Preview Complete`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={previewPage >= previewPages.length}
                        onClick={() => setPreviewPage(p => p + 1)}
                      >
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    Preview not available for this book
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div className="px-6 pb-5">
                <Button
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700"
                  onClick={() => { addToCart(previewBook); closePreview(); }}
                  data-testid="preview-add-to-cart"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart - ${previewBook.price}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Books;
